import { CheckCircle2, XCircle, Clock, Phone, MapPin, Calendar, User, Users, DollarSign, Truck, Zap, Mail, Trash2, HandCoins, AlertCircle, Shield, ArrowRightLeft, PencilLine, Link2 } from "lucide-react";
import { STATUS_TABS, money, titleCase } from "./utils";

const ACTION_LABEL = {
  no_hold_placed: "No hold placed",
  collect_balance: "Balance owed",
  card_declined: "Card declined",
  manual_review: "Manual review",
};

export function RideCard({ ride, selectable, selected, onSelect, onComplete, onCancel, onAssign, onNearest, onSplit, onInvoice, onDelete, onChangeHold, onRestoreNoShow, onPaymentAction, onSendPaymentLink, onOpenPatient, onApproveCash, onForceHeld, onWaiveHold, onOverrideStatus, onAttachCapturedPi, onResendConfirmation, onEdit }) {
  const tab = STATUS_TABS.find((t) => t.key === ride.status);
  const StatusIcon = tab?.icon || Clock;
  return (
    <div className={`bg-white border rounded-2xl p-5 md:p-6 ${ride.action_needed ? "border-red-400 ring-2 ring-red-200" : selected ? "border-brand-green ring-2 ring-brand-green/30" : "border-brand-navy/10"}`} data-testid={`ride-card-${ride.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          {selectable && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={(e) => onSelect(ride, e.target.checked)}
              data-testid={`select-ride-${ride.id}`}
              className="mt-1 w-4 h-4 accent-brand-green cursor-pointer"
              aria-label={`Select ride for ${ride.member_name}`}
            />
          )}
          <div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-brand-green" />
              {onOpenPatient && ride.patient_id ? (
                <button
                  onClick={() => onOpenPatient(ride.patient_id)}
                  data-testid={`open-patient-${ride.id}`}
                  className="font-serif text-xl text-brand-deep leading-tight hover:text-brand-green underline decoration-dotted underline-offset-4 text-left"
                >
                  {ride.member_name}
                </button>
              ) : (
                <span className="font-serif text-xl text-brand-deep leading-tight">{ride.member_name}</span>
              )}
            </div>
            <div className="text-xs text-brand-muted mt-1 font-mono">ID: {ride.id?.slice(0, 8)}…</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {ride.preferred_payment_method === "cash" && (
            <span
              className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-400"
              data-testid={`cash-pickup-badge-${ride.id}`}
              title="Cash payment — driver collects at destination"
            >
              <HandCoins className="inline w-3 h-3 mr-1 -mt-0.5" />
              Cash pickup
            </span>
          )}
          {(ride.preferred_payment_method === "zelle" || ride.preferred_payment_method === "check") && (
            <span
              className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-900 border border-blue-300"
              data-testid={`offline-pay-badge-${ride.id}`}
            >
              {ride.preferred_payment_method === "zelle" ? "Zelle" : "Check"} pickup
            </span>
          )}
          {ride.action_needed && (
            <span
              className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-900 border border-red-300"
              data-testid={`action-needed-badge-${ride.id}`}
              title="Payment recovery required — click Resolve payment"
            >
              <AlertCircle className="inline w-3 h-3 mr-1 -mt-0.5" />
              Action needed{ride.action_reason ? ` · ${ACTION_LABEL[ride.action_reason] || ride.action_reason.replace(/_/g, " ")}` : ""}
              {ride.balance_owed_cents ? ` · ${money(ride.balance_owed_cents)}` : ""}
            </span>
          )}
          {ride.on_call && (
            <span
              className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-rose-100 text-rose-900 border-2 border-rose-400 animate-pulse"
              data-testid={`on-call-badge-${ride.id}`}
              title="Discharge — on-call priority. Dispatch stays on standby until the nurse calls."
            >
              <AlertCircle className="inline w-3 h-3 mr-1 -mt-0.5" />
              Discharge · on-call
            </span>
          )}
          {ride.hold_waived && (
            <span
              className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border-2 border-amber-400"
              data-testid={`hold-waived-badge-${ride.id}`}
              title={`Hold waived by ${ride.hold_waived_by || "dispatcher"}${ride.hold_waived_reason ? " — " + ride.hold_waived_reason : ""}${ride.hold_waived_at ? " · " + new Date(ride.hold_waived_at).toLocaleString() : ""}`}
            >
              <HandCoins className="inline w-3 h-3 mr-1 -mt-0.5" />
              Hold waived · pay at pickup
            </span>
          )}
          {ride.status_override_at && (
            <span
              className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-900 border border-slate-400"
              data-testid={`status-override-badge-${ride.id}`}
              title={`Moved by ${ride.status_override_by || "dispatch"} — ${ride.status_override_reason || "no reason"} · ${new Date(ride.status_override_at).toLocaleString()}`}
            >
              <Shield className="inline w-3 h-3 mr-1 -mt-0.5" />
              Manually moved
            </span>
          )}
          {ride.payment_mode === "on_arrival" && (
            <span
              className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-orange-100 text-orange-900 border border-orange-300"
              data-testid={`payonarrival-badge-${ride.id}`}
              title="Customer pays driver directly. Stripe hold is a guarantee only."
            >
              <HandCoins className="inline w-3 h-3 mr-1 -mt-0.5" />
              Pay on arrival
            </span>
          )}
          {ride.cancel_reason === "no_show" && (
            <span
              className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-900 border border-red-300"
              data-testid={`no-show-badge-${ride.id}`}
              title={ride.no_show_auto_flagged ? "Auto-flagged by system after 20+ min at pickup" : "Marked no-show by dispatch"}
            >
              {ride.no_show_auto_flagged ? "Auto no-show" : "No-show"}
            </span>
          )}
          {ride.auto_assigned && (
            <span
              className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-teal-100 text-teal-900 border border-teal-300"
              data-testid={`auto-badge-${ride.id}`}
              title={ride.auto_assigned_distance_miles ? `Auto-routed to nearest driver · ${ride.auto_assigned_distance_miles} mi from pickup` : "Auto-routed to nearest driver"}
            >
              <Zap className="inline w-3 h-3 mr-1 -mt-0.5" />
              Auto-routed{ride.auto_assigned_distance_miles ? ` · ${ride.auto_assigned_distance_miles} mi` : ""}
            </span>
          )}
          {ride.sibling_group_id && ride.sibling_total > 1 && (
            <span className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300" data-testid={`sibling-badge-${ride.id}`}>
              <Users className="inline w-3 h-3 mr-1 -mt-0.5" />
              Trip {ride.sibling_index} of {ride.sibling_total}
            </span>
          )}
          <span className={`text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full border ${tab?.tone || "bg-slate-100 text-slate-700 border-slate-300"}`}>
            <StatusIcon className="inline w-3 h-3 mr-1 -mt-0.5" />
            {ride.status?.replace(/_/g, " ")}
          </span>
          {ride.capture_source === "auto" && (
            <span className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300">Auto</span>
          )}
          {ride.driver_id && ride.driver_ride_status && ride.driver_ride_status !== "completed" && (
            <span className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300">
              <Truck className="inline w-3 h-3 mr-1 -mt-0.5" />
              {ride.driver_name}: {(ride.driver_ride_status || "").replace(/_/g, " ")}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-brand-deep">
        <div className="flex items-start gap-2"><Phone className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" /><span>{ride.member_phone}</span></div>
        <div className="flex items-start gap-2"><Calendar className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" /><span>{ride.trip_date} · {ride.trip_time}</span></div>
        <div className="flex items-start gap-2 md:col-span-2"><MapPin className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" /><span><strong>Pickup:</strong> {ride.pickup}</span></div>
        <div className="flex items-start gap-2 md:col-span-2"><MapPin className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" /><span><strong>Destination:</strong> {ride.dropoff}</span></div>
        <div className="flex items-start gap-2"><DollarSign className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" />
          <span>
            {ride.status === "completed" && ride.captured_amount_cents
              ? (ride.balance_owed_cents
                  ? <>Captured <strong>{money(ride.captured_amount_cents)}</strong> · <span className="text-red-700 font-semibold">Balance {money(ride.balance_owed_cents)}</span></>
                  : <>Captured <strong>{money(ride.captured_amount_cents)}</strong>{ride.final_fare_cents ? <> of {money(ride.final_fare_cents)}</> : <> of {money(ride.hold_amount_cents)}</>}</>)
              : ride.status === "cancelled"
                ? <>Fee retained <strong>{money(ride.cancellation_fee_cents)}</strong></>
                : <>Hold: <strong>{money(ride.hold_amount_cents)}</strong>{ride.final_fare_cents ? <> · Final fare {money(ride.final_fare_cents)}</> : null}</>}
          </span>
        </div>
        {/* SUGGESTED FINAL FARE — computed by the trip calculator using
            actual mileage + stops + wait time. Shown so dispatch knows
            exactly how much to charge on completion without rebuilding. */}
        {ride.suggested_final_fare_cents && ride.status !== "completed" && ride.status !== "cancelled" && (
          <div className="flex items-start gap-2 md:col-span-2" data-testid={`suggested-fare-${ride.id}`}>
            <DollarSign className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
            <span className="text-emerald-800">
              <strong>Charge on completion: {money(ride.suggested_final_fare_cents)}</strong>
              {ride.total_miles ? <span className="text-brand-muted"> · {ride.total_miles} mi total</span> : null}
              {(ride.stops && ride.stops.length) ? <span className="text-brand-muted"> · {ride.stops.length} stop{ride.stops.length === 1 ? "" : "s"}</span> : null}
            </span>
          </div>
        )}
        {ride.signature_data_url && (
          <div className="flex items-start gap-2 md:col-span-2" data-testid={`sig-badge-${ride.id}`}>
            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <span className="text-xs text-emerald-700">
              Signature captured{ride.signed_by_name ? ` — ${ride.signed_by_name}` : ""}.
              <a href={ride.signature_data_url} target="_blank" rel="noreferrer" className="ml-1 underline">View</a>
            </span>
          </div>
        )}
        <div className="text-xs text-brand-muted">{titleCase(ride.trip_class)} · {ride.mobility}{ride.return_trip && ride.return_trip !== "none" ? ` · Return ${ride.return_trip}` : ""}</div>
        {ride.special_needs && <div className="md:col-span-2 text-xs text-brand-muted">Special: {ride.special_needs}</div>}
        {ride.notes && <div className="md:col-span-2 text-xs text-brand-muted italic">Notes: {ride.notes}</div>}
      </div>

      {ride.status === "pending_payment" && (
        <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid={`pending-payment-notice-${ride.id}`}>
          <div className="flex items-start gap-2.5">
            <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 flex-1 min-w-0">
              <div className="font-bold uppercase tracking-[0.14em] text-[10px] mb-0.5">Waiting for customer to pay</div>
              The customer hasn't authorized their card yet — a driver can't be assigned until the hold lands. Tap <strong>Send payment link</strong> below to email them a fresh Stripe Checkout, or if you can SEE the hold in Stripe already, use <strong>Payment confirmed</strong>.
            </div>
          </div>
          {onForceHeld && (
            <div className="mt-3 pl-7">
              <button
                onClick={() => onForceHeld(ride)}
                data-testid={`force-held-${ride.id}`}
                className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-4 py-2 rounded-full"
                title="Use this when Stripe Dashboard shows the hold but our webhook didn't fire. Flips status to Held so you can assign a driver."
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Payment confirmed — mark as Held
              </button>
            </div>
          )}
        </div>
      )}

      {ride.status === "held" && (
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            onClick={() => onNearest(ride)}
            data-testid={`nearest-ride-${ride.id}`}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-4 py-2 rounded-full transition"
          >
            <Zap className="w-4 h-4" /> Nearest driver
          </button>
          <button
            onClick={() => onAssign(ride)}
            data-testid={`assign-ride-${ride.id}`}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-full transition"
          >
            <Truck className="w-4 h-4" /> {ride.driver_id ? (ride.auto_assigned ? "Override & reassign" : "Reassign") : "Pick from list"}
          </button>
          {!ride.sibling_group_id && (
            <button
              onClick={() => onSplit(ride)}
              data-testid={`split-ride-${ride.id}`}
              className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white text-sm font-medium px-4 py-2 rounded-full transition"
            >
              <Users className="w-4 h-4" /> Split into N rides
            </button>
          )}
          <button
            onClick={() => onComplete(ride)}
            data-testid={`complete-ride-${ride.id}`}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-full transition"
          >
            <CheckCircle2 className="w-4 h-4" /> Complete & capture
          </button>
          <button
            onClick={() => onCancel(ride)}
            data-testid={`cancel-ride-${ride.id}`}
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-full transition"
          >
            <XCircle className="w-4 h-4" /> Cancel with fee
          </button>
        </div>
      )}

      {ride.status === "insurance_pending" && (
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            onClick={() => onNearest(ride)}
            data-testid={`nearest-ride-${ride.id}`}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-4 py-2 rounded-full transition"
          >
            <Zap className="w-4 h-4" /> Nearest driver
          </button>
          <button
            onClick={() => onAssign(ride)}
            data-testid={`assign-ride-${ride.id}`}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-full transition"
          >
            <Truck className="w-4 h-4" /> {ride.driver_id ? "Reassign" : "Pick from list"}
          </button>
        </div>
      )}

      {ride.status === "completed" && (
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            onClick={() => onInvoice(ride)}
            data-testid={`invoice-ride-${ride.id}`}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-full transition"
          >
            <Mail className="w-4 h-4" /> Send invoice
          </button>
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-brand-navy/10 flex justify-between items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {ride.preferred_payment_method === "cash" && !ride.cash_approved && onApproveCash && (
            <button
              onClick={() => onApproveCash(ride)}
              data-testid={`approve-cash-${ride.id}`}
              className="inline-flex items-center gap-1.5 text-xs bg-amber-600 hover:bg-amber-700 text-white font-semibold px-3 py-1.5 rounded-full transition"
              title="Confirm cash pickup with the customer, then approve"
            >
              <HandCoins className="w-3.5 h-3.5" /> Approve cash
            </button>
          )}
          {ride.preferred_payment_method === "cash" && ride.cash_approved && (
            <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold px-2.5 py-1 rounded-full" data-testid={`cash-approved-${ride.id}`}>
              <HandCoins className="w-3 h-3" /> Cash approved
            </span>
          )}
          {ride.action_needed && onPaymentAction && (
            <button
              onClick={() => onPaymentAction(ride)}
              data-testid={`resolve-payment-${ride.id}`}
              className="inline-flex items-center gap-1.5 text-xs bg-red-600 hover:bg-red-700 text-white font-semibold px-3 py-1.5 rounded-full transition"
              title="Charge saved card, send payment link, or record offline payment"
            >
              <AlertCircle className="w-3.5 h-3.5" /> Resolve payment
            </button>
          )}
          {ride.status === "pending_payment" && onSendPaymentLink && (
            <button
              onClick={() => onSendPaymentLink(ride)}
              data-testid={`send-hold-link-${ride.id}`}
              className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 rounded-full transition"
              title="Email a fresh Stripe Checkout link for the hold amount"
            >
              <Mail className="w-3.5 h-3.5" /> Send payment link
            </button>
          )}
          {(ride.status === "held" || ride.status === "pending_payment") && (
            <button
              onClick={() => onChangeHold(ride)}
              data-testid={`change-hold-${ride.id}`}
              className="inline-flex items-center gap-1.5 text-xs text-brand-navy hover:text-brand-green px-3 py-1.5 rounded-full border border-brand-navy/15 hover:border-brand-green transition"
              title="Cancel current hold and send customer a new checkout link at a new amount"
            >
              <DollarSign className="w-3.5 h-3.5" /> Change hold
            </button>
          )}
          {ride.status === "cancelled" && ride.cancel_reason === "no_show" && (
            <button
              onClick={() => onRestoreNoShow(ride)}
              data-testid={`restore-no-show-${ride.id}`}
              className="inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:text-white hover:bg-emerald-600 px-3 py-1.5 rounded-full border border-emerald-500 transition font-semibold"
              title="Un-cancel this no-show, refund any fee, and put it back on the board"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Restore from no-show
            </button>
          )}
          {!ride.hold_waived && !ride.captured_at && ride.status !== "completed" && ride.status !== "cancelled" && onWaiveHold && (
            <button
              onClick={() => onWaiveHold(ride)}
              data-testid={`waive-hold-${ride.id}`}
              className="inline-flex items-center gap-1.5 text-xs bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold px-3 py-1.5 rounded-full border-2 border-amber-400 transition"
              title="Waive the card hold on this ride (service recovery / VIP override — reason required)"
            >
              <HandCoins className="w-3.5 h-3.5" /> Waive hold
            </button>
          )}
          {onEdit && (
            <button
              onClick={() => onEdit(ride)}
              data-testid={`edit-ride-${ride.id}`}
              className="inline-flex items-center gap-1.5 text-xs text-brand-navy hover:text-brand-green px-3 py-1.5 rounded-full border border-brand-navy/15 hover:border-brand-green transition"
              title="Edit ride details, addresses, times, or pricing"
            >
              <PencilLine className="w-3.5 h-3.5" /> Edit
            </button>
          )}
          {onOverrideStatus && (
            <button
              onClick={() => onOverrideStatus(ride)}
              data-testid={`override-status-${ride.id}`}
              className="inline-flex items-center gap-1.5 text-xs text-slate-700 hover:text-white hover:bg-slate-700 px-3 py-1.5 rounded-full border border-slate-300 transition"
              title="Super-admin: force-move this ride to any status (audit-logged)"
            >
              <ArrowRightLeft className="w-3.5 h-3.5" /> Move to…
            </button>
          )}
          {onAttachCapturedPi && !ride.captured_at && ride.status !== "completed" && ride.status !== "cancelled" && (
            <button
              onClick={() => onAttachCapturedPi(ride)}
              data-testid={`attach-captured-pi-${ride.id}`}
              className="inline-flex items-center gap-1.5 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-900 font-semibold px-3 py-1.5 rounded-full border-2 border-emerald-400 transition"
              title="Customer paid on Stripe but the ride isn't linked — paste the pi_ id to attach it and mark completed"
            >
              <Link2 className="w-3.5 h-3.5" /> Attach paid Stripe
            </button>
          )}
          {ride.member_email && onResendConfirmation && (
            <button
              onClick={() => onResendConfirmation(ride)}
              data-testid={`resend-confirmation-${ride.id}`}
              className="inline-flex items-center gap-1.5 text-xs text-brand-navy hover:text-brand-green px-3 py-1.5 rounded-full border border-brand-navy/15 hover:border-brand-green transition"
              title={`Email the confirmation to ${ride.member_email}`}
            >
              <Mail className="w-3.5 h-3.5" /> Email confirmation
            </button>
          )}
        </div>
        <button
          onClick={() => onDelete(ride)}
          data-testid={`delete-ride-${ride.id}`}
          className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-full transition"
          title="Archive this ride (recoverable — kept in audit log)"
        >
          <Trash2 className="w-3.5 h-3.5" /> Archive ride
        </button>
      </div>
    </div>
  );
}
